"use client";

import { ProductTileTemplate } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/__internal__/ProductTileTemplate";
import { IServerRenderedTile } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/types";
import { useServerRenderedProductTiles } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/ServerRenderedProductTilesContext";
import { useIsLoggedInRetailer } from "@faire/marketplace-lib/serialized-data/isLoggedInRetailer";
import { Surface } from "@faire/web-api--source/com/faire/logging/Surface";
import { trackSearchProductBatchView } from "@faire/web-api--source/events/search/view/productBatch";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import { ProductTileContainer } from "@faire/web-api--source/indigofair/data/ProductTileContainer";
import { ProductTileScreenContainer } from "@faire/web-api--source/indigofair/data/ProductTileScreenContainer";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import React, { use } from "react";

import { LoggedOutSearchTileWrapper } from "../../search/__internal__/SearchProductsPage/__internal__/LoggedOutSearchTileWrapper";
import { useSearchTileInteractions } from "../../search/__internal__/SearchProductsPage/__internal__/utils";

export const MaybeServerRenderedProductTileWrapper = ({
  index,
  productTile,
  brand,
  requestId,
  productTokens,
  context,
  productTileContainer,
  contextToken,
  screenContext,
  containerContext,
  containerName,
  isIrrelevantProductTile,
}: {
  index: number;
  productTile: ILeanProductTile | undefined;
  brand: ILeanProductTile.IBrand | undefined;
  requestId: string | undefined;
  productTokens: (string | undefined)[] | undefined;
  context: `${keyof typeof ProductTileContainer}|${string}`;
  productTileContainer: ProductTileContainer;
  contextToken: string;
  screenContext: Surface;
  containerContext: ProductTileScreenContainer;
  containerName: string;
  isIrrelevantProductTile: boolean;
}) => {
  const viewRef = useDoActionWhenVisible(() => {
    const productToken = productTokens?.[index] ?? productTile?.product?.token;
    if (productToken) {
      trackSearchProductBatchView(requestId ?? "", productToken);
    }
  });

  return (
    <div ref={viewRef}>
      <MaybeServerRenderedProductTileWrapperBase
        index={index}
        productTile={productTile}
        brand={brand}
        requestId={requestId}
        context={context}
        productTileContainer={productTileContainer}
        contextToken={contextToken}
        screenContext={screenContext}
        containerContext={containerContext}
        containerName={containerName}
        isIrrelevantProductTile={isIrrelevantProductTile}
      />
    </div>
  );
};

const MaybeServerRenderedProductTileWrapperBase = ({
  index,
  productTile,
  brand,
  requestId,
  context,
  productTileContainer,
  containerName,
  contextToken,
  screenContext,
  containerContext,
  isIrrelevantProductTile,
}: {
  index: number;
  productTile: ILeanProductTile | undefined;
  brand: ILeanProductTile.IBrand | undefined;
  requestId: string | undefined;
  context: `${keyof typeof ProductTileContainer}|${string}`;
  productTileContainer: ProductTileContainer;
  containerName: string;
  contextToken: string;
  screenContext: Surface;
  containerContext: ProductTileScreenContainer;
  isIrrelevantProductTile: boolean;
}) => {
  const isLoggedInRetailer = useIsLoggedInRetailer();
  if (!isLoggedInRetailer && productTile) {
    return (
      <LoggedOutSearchTileWrapper
        productTile={productTile}
        index={index}
        brand={brand}
        requestId={requestId}
        containerName={containerName}
        productTileScreen={screenContext}
        context={context}
        productTileContainer={productTileContainer}
        contextToken={contextToken}
        containerContext={containerContext}
      />
    );
  }

  return (
    <ServerRenderedProductTileWrapper
      index={index}
      productTile={productTile}
      brand={brand}
      requestId={requestId}
      context={context}
      productTileContainer={productTileContainer}
      containerName={containerName}
      contextToken={contextToken}
      screenContext={screenContext}
      containerContext={containerContext}
      isIrrelevantProductTile={isIrrelevantProductTile}
    />
  );
};

const ServerRenderedProductTileWrapper = ({
  index,
  productTile,
  brand,
  requestId,
  context,
  productTileContainer,
  containerName,
  contextToken,
  screenContext,
  containerContext,
  isIrrelevantProductTile,
}: {
  index: number;
  productTile: ILeanProductTile | undefined;
  brand: ILeanProductTile.IBrand | undefined;
  requestId: string | undefined;
  context: `${keyof typeof ProductTileContainer}|${string}`;
  productTileContainer: ProductTileContainer;
  containerName: string;
  contextToken: string;
  screenContext: Surface;
  containerContext: ProductTileScreenContainer;
  isIrrelevantProductTile: boolean;
}) => {
  // Slot the RSC Product Tile by returning the context value
  // Note that useServerRenderedProductTiles returns an array of RSC Product Tiles
  // If the product tile is not provided for the index, then return the child client tile
  const BATCH_SIZE = 12;
  const serverRenderedProductTiles = useServerRenderedProductTiles();

  const getServerRenderedProductTile = (): IServerRenderedTile | undefined => {
    if (isIrrelevantProductTile) {
      return serverRenderedProductTiles?.serverRenderedIrrelevantProductTiles?.[
        index
      ];
    }
    let serverRenderedProductTile;
    if (index < BATCH_SIZE) {
      serverRenderedProductTile =
        serverRenderedProductTiles?.firstServerRenderedProductTilesBatch
          ?.serverRenderedProductTiles?.[index];
    } else {
      const remainingTilesPromise =
        serverRenderedProductTiles?.remainingServerRenderedProductTiles;
      if (remainingTilesPromise) {
        const remainingTiles = use(remainingTilesPromise);
        serverRenderedProductTile =
          remainingTiles?.serverRenderedProductTiles?.[index - BATCH_SIZE];
      }
    }
    return serverRenderedProductTile;
  };

  const serverRenderedProductTile = getServerRenderedProductTile();
  const { tileActions, props } = useSearchTileInteractions(
    productTile,
    brand?.token
  );

  const fallbackTile = productTile ? (
    <LoggedOutSearchTileWrapper
      brand={brand}
      productTile={productTile}
      index={index}
      requestId={requestId}
      containerName={containerName}
      productTileScreen={screenContext}
      context={context}
      productTileContainer={productTileContainer}
      contextToken={contextToken}
      containerContext={containerContext}
    />
  ) : null;

  if (serverRenderedProductTile) {
    return (
      <ProductTileTemplate
        loggingContext={{
          impressionProviderProps: {
            containerContext,
            context,
            requestId,
            screenContext,
            containerName,
            productTileContainer,
            contextToken,
            trackedLinkContainerName: containerName,
          },
        }}
        trackingProps={serverRenderedProductTile?.trackingProps}
        tileInteractions={{
          tileActions: tileActions,
          props: props,
        }}
        topSlot={serverRenderedProductTile?.topSlot}
        overlaySlot={serverRenderedProductTile?.overlaySlot}
        middleSlot={serverRenderedProductTile?.middleSlot}
        bottomSlot={serverRenderedProductTile?.bottomSlot}
      />
    );
  } else {
    return fallbackTile;
  }
};
